import { ReplaySubject } from "rxjs";
import { type AnyActorRef } from "xstate";

import { observableToAsyncGenerator } from "@moment/api-collab/observableToAsyncGenerator";

const bridges = new Map<
	string,
	StateMachineRuntimeBridge<Pick<AnyActorRef, "subscribe" | "getSnapshot"> | undefined, unknown>
>();

export declare function useSelector<
	TActor extends Pick<AnyActorRef, "subscribe" | "getSnapshot"> | undefined,
	T,
>(
	actor: TActor,
	selector: (
		snapshot: TActor extends {
			getSnapshot(): infer TSnapshot;
		}
			? TSnapshot
			: undefined
	) => T,
	compare?: (a: T, b: T) => boolean
): T;

export const useStateMachineRuntimeBridge = <
	TActor extends Pick<AnyActorRef, "subscribe" | "getSnapshot"> | undefined,
	T,
>(
	key: string,
	machine: TActor,
	selector: (
		snapshot: TActor extends {
			getSnapshot(): infer TSnapshot;
		}
			? TSnapshot
			: undefined
	) => T
): StateMachineRuntimeBridge<TActor, T> => {
	const bridge = bridges.get(key);
	if (bridge && bridge.machine !== machine) {
		bridge.replaceMachine(machine);
	}

	if (bridge) {
		// This is a no-op, it exists to make sure we're not accidentally returning something like
		// `undefined` and masking it with a cast.
		bridge satisfies StateMachineRuntimeBridge<
			Pick<AnyActorRef, "subscribe" | "getSnapshot"> | undefined,
			unknown
		>;
		return bridge as StateMachineRuntimeBridge<TActor, T>;
	}

	const newBridge = new StateMachineRuntimeBridge<TActor, T>(machine, selector);
	bridges.set(key, newBridge as StateMachineRuntimeBridge<TActor, unknown>);

	return newBridge;
};

/**
 * A general-purpose bridge between the push-based `PageStorageSubscribable` and the pull-oriented
 * `AsyncIterable`-based runtime.
 *
 * Intuitively, both local and collaborative implementations of page storage use RxJS `Observable`s
 * to "push" values out to subscribers. For the runtime to get these valuses, though, they need to
 * be exposed as pull-based async generator functions. This class simply bridges the two paradigms,
 * turning the `Observable` into an `AsyncIterableIterator`.
 */
export class StateMachineRuntimeBridge<
	TActor extends Pick<AnyActorRef, "subscribe" | "getSnapshot"> | undefined,
	T,
> {
	/**
	 * EXTREMELY IMPORTANT: `ReplaySubject` because `pageStorage` needs to emit the current value of
	 * page storage, whenever it subscribes. If you do not do this, the fetch of page storage data
	 * will sometimes complete before the `pageStorage` subscription is created, which causes
	 * `pageStorage` to hang forever in the app. Or at least, hang until you mutate it.
	 */
	private _dataUpdates = new ReplaySubject<T>(1);
	private _clientSubscription: { unsubscribe: () => void };

	constructor(
		public machine: TActor,
		private _selector: (
			snapshot: TActor extends {
				getSnapshot(): infer TSnapshot;
			}
				? TSnapshot
				: undefined
		) => T
	) {
		this._clientSubscription = this.machine?.subscribe((snap) => {
			const u = this._selector(snap);
			this._dataUpdates.next(u);
		}) ?? { unsubscribe: () => {} };
	}

	data = (): AsyncIterableIterator<T> => {
		async function* lastResultThenValues(
			bridge: StateMachineRuntimeBridge<TActor, T>
		): AsyncIterableIterator<T> {
			try {
				yield bridge._selector(bridge.machine?.getSnapshot());
				yield* observableToAsyncGenerator(bridge._dataUpdates);
			} finally {
				bridge.dispose();
			}
		}

		return lastResultThenValues(this);
	};

	dispose() {
		this._dataUpdates.complete();
		this._clientSubscription?.unsubscribe();
	}

	replaceMachine(newMachine: TActor) {
		// Unsubscribe from old machine
		this._clientSubscription?.unsubscribe();

		// Update machine reference
		this.machine = newMachine;

		// Create new subscription
		this._clientSubscription = this.machine?.subscribe((snap) => {
			const u = this._selector(snap);
			this._dataUpdates.next(u);
		}) ?? { unsubscribe: () => {} };

		// Emit initial state from new machine
		const initialValue = this._selector(this.machine?.getSnapshot());
		this._dataUpdates.next(initialValue);
	}
}
